import type { AxiosError } from 'axios';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

export function handleApiError(error: unknown): ApiError {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    const message = data?.error || data?.message;

    if (status === 401) {
      return {
        message: message || '인증이 필요합니다. 다시 로그인해주세요.',
        status,
        code: 'UNAUTHORIZED',
      };
    }

    if (status === 403) {
      return {
        message: message || '이 작업을 수행할 권한이 없습니다.',
        status,
        code: 'FORBIDDEN',
      };
    }

    if (status === 404) {
      return {
        message: message || '요청한 리소스를 찾을 수 없습니다.',
        status,
        code: 'NOT_FOUND',
      };
    }

    if (status === 429) {
      return {
        message: message || '너무 많은 요청을 보냈습니다. 잠시 후 다시 시도해주세요.',
        status,
        code: 'RATE_LIMIT',
      };
    }

    if (status && status >= 500) {
      return {
        message: message || '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        status,
        code: 'SERVER_ERROR',
      };
    }

    return {
      message: message || '요청 처리 중 오류가 발생했습니다.',
      status,
      code: 'API_ERROR',
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      code: 'UNKNOWN_ERROR',
    };
  }

  return {
    message: '알 수 없는 오류가 발생했습니다.',
    code: 'UNKNOWN_ERROR',
  };
}

function isAxiosError(error: unknown): error is AxiosError {
  return !!error && (error as AxiosError).isAxiosError === true;
}

export function getErrorMessage(error: unknown): string {
  return handleApiError(error).message;
}
